import { Button, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useAdvisors } from 'src/clients/hooks/useAdvisors';
import { useReassignFamilyAdvisor } from 'src/clients/hooks/useReassignFamilyAdvisor';
import { Logger } from 'src/utils/Logger';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

type Props = {
  familyId: string;
  currentAdvisorId?: string;
  opened: boolean;
  onClose: () => void;
};

export function ChangeFamilyAdvisorModal({ familyId, currentAdvisorId, opened, onClose }: Props) {
  const { advisors, isLoading: advisorsLoading } = useAdvisors();
  const {
    reassignFamilyAdvisor,
    isLoading: isReassigning,
    error,
    reset,
  } = useReassignFamilyAdvisor();
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string | null>(null);

  function handleSelect(value: string | null) {
    reset();
    setSelectedAdvisorId(value);
  }

  async function handleConfirm() {
    if (!selectedAdvisorId) return;
    try {
      await reassignFamilyAdvisor(familyId, selectedAdvisorId);
      const advisor = advisors.find(a => a.id === selectedAdvisorId);
      notifications.show({
        title: 'Advisor changed',
        message: `The whole family has been moved to ${formatName(
          advisor?.firstName,
          advisor?.lastName,
          NameFormat.NATURAL,
        )}.`,
        color: 'green',
      });
      onClose();
    } catch (e) {
      Logger.error('Failed to change family advisor', e);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Change advisor" centered>
      <Stack gap="md">
        <Text size="sm">
          The whole family must share one advisor. Choosing a new advisor moves every member of this
          family to them.
        </Text>
        <Select
          label="New advisor"
          placeholder="Select an advisor"
          data={advisors.map(advisor => ({
            value: advisor.id,
            label: formatName(advisor.firstName, advisor.lastName, NameFormat.NATURAL),
          }))}
          value={selectedAdvisorId}
          onChange={handleSelect}
          searchable
          required
          error={error ? 'Could not change the advisor. Please try again.' : undefined}
        />
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={isReassigning}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={advisorsLoading || isReassigning}
            disabled={!selectedAdvisorId || selectedAdvisorId === currentAdvisorId}
          >
            Change advisor
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
